import React, { useMemo } from "react";
import Amount from "ui-components/molecules/amount/amount";
import Loading from "ui-components/loading/loading";
import { PlanningFreightProbill } from "app/data/linehaul-outbound-planning/models";
import "../../freights/inbound/inboundFreight.scss";
import "../outboundPlanningList.scss";

interface SummaryTile {
  id: string;
  label: string;
  value: React.ReactNode;
}

interface PlanningFreightListSummaryProps {
  rows: PlanningFreightProbill[] | null | undefined;
  /** Include Handling Units / Rolls / Non-Rolled (not used for pickups). */
  includeHandlingUnits?: boolean;
  /** When set, HU / roll counts use these rows instead of `rows` (e.g. exclude pickups). */
  handlingUnitRows?: PlanningFreightProbill[] | null;
  /** Label for the row-count tile, e.g. "Probills", "Shipments", "Pickups". */
  countLabel?: string;
  title?: string;
  isLoading?: boolean;
}

function formatCount(value: number) {
  return value.toLocaleString("en", { useGrouping: true });
}

function formatWeightLbs(value: number) {
  return `${formatCount(value)} lbs`;
}

function sumField(
  rows: PlanningFreightProbill[] | null | undefined,
  field: keyof PlanningFreightProbill
) {
  return (rows ?? []).reduce((sum, row) => sum + (Number(row[field]) || 0), 0);
}

function summarizeHandlingUnits(rows: PlanningFreightProbill[] | null | undefined) {
  let handlingUnitCount = 0;
  let rollCount = 0;
  let nonRolledCount = 0;

  for (const row of rows ?? []) {
    for (const item of row.items ?? []) {
      handlingUnitCount += 1;
      const isRoll = String(item.itemIsRoll ?? "").trim().toUpperCase();
      if (isRoll === "Y") rollCount += 1;
      else if (isRoll === "N") nonRolledCount += 1;
    }
  }

  return { handlingUnitCount, rollCount, nonRolledCount };
}

const PlanningFreightListSummary: React.FC<PlanningFreightListSummaryProps> = ({
  rows,
  includeHandlingUnits = false,
  handlingUnitRows,
  countLabel = "Probills",
  title = "Summary",
  isLoading = false,
}) => {
  const tiles = useMemo((): SummaryTile[] => {
    const count = (rows ?? []).length;
    const weight = sumField(rows, "probillWeight");
    const pieces = sumField(rows, "probillPieces");
    const freightCharge = sumField(rows, "probillFreightCharge");
    const result: SummaryTile[] = [
      { id: "count", label: countLabel, value: formatCount(count) },
    ];

    if (includeHandlingUnits) {
      const hu = summarizeHandlingUnits(handlingUnitRows ?? rows);
      result.push(
        { id: "hu", label: "Handling Units", value: formatCount(hu.handlingUnitCount) },
        { id: "rolls", label: "Rolls", value: formatCount(hu.rollCount) },
        { id: "nonRolled", label: "Non-Rolled", value: formatCount(hu.nonRolledCount) }
      );
    }

    result.push(
      { id: "weight", label: "Weight", value: formatWeightLbs(weight) },
      { id: "pieces", label: "Pieces", value: formatCount(pieces) },
      {
        id: "freightCharge",
        label: "Freight Charge",
        value: <Amount amount={freightCharge} />,
      }
    );
    return result;
  }, [rows, includeHandlingUnits, handlingUnitRows, countLabel]);

  return (
    <div
      className="planning-freight-list-summary"
      role="group"
      aria-label={title}
    >
      <div className="planning-freight-list-summary__header">{title}</div>
      <div className="inbound-freights planning-freight-list-summary__tiles">
        {tiles.map(({ id, label, value }) => (
          <div key={id} className="inbound-freights__tile">
            <div>
              {isLoading ? (
                <Loading isLoading />
              ) : (
                <div className="inbound-freights__tile__count">{value}</div>
              )}
              <div className="inbound-freights__tile__text">{label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlanningFreightListSummary;
